import {
  createLocalTracks,
  type LocalTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import {
  trackAvatarConnected,
  trackAvatarDisconnected,
  trackError,
} from "./analytics";
import type { WidgetState } from "./types";

export interface AvatarCallbacks {
  onStreamReady?: () => void;
  onDisconnected?: () => void;
  onAgentSpeaking?: () => void;
  onAgentListening?: () => void;
  onError?: (error: string) => void;
  onStateChange?: (state: WidgetState) => void;
}

export interface LiveKitTokenResponse {
  token: string;
  serverUrl: string;
  roomName: string;
  participantName: string;
}

export interface HedraAvatarConfig {
  tokenResponse: LiveKitTokenResponse;
  videoElement: HTMLVideoElement;
  callbacks?: AvatarCallbacks;
}

// Agent state messages (sent by Python worker)
const AGENT_MESSAGES = {
  SPEAKING: "__AGENT_SPEAKING__",
  LISTENING: "__AGENT_LISTENING__",
  THINKING: "__AGENT_THINKING__",
} as const;

export class HedraAvatarManager {
  private room: Room | null = null;
  private videoElement: HTMLVideoElement;
  private callbacks: AvatarCallbacks;
  private connectionStartTime: number | null = null;
  private localAudioTrack: LocalTrack | null = null;

  constructor(config: HedraAvatarConfig) {
    this.videoElement = config.videoElement;
    this.callbacks = config.callbacks ?? {};
  }

  async initialize(tokenResponse: LiveKitTokenResponse): Promise<void> {
    if (this.room) {
      console.warn("Room already connected");
      return;
    }

    try {
      this.callbacks.onStateChange?.("loading");
      this.connectionStartTime = Date.now();

      console.log("Connecting to LiveKit room:", tokenResponse.roomName);

      this.room = new Room();
      this.setupEventListeners();

      await this.room.connect(tokenResponse.serverUrl, tokenResponse.token);
      console.log("Connected to LiveKit room");

      // Publish local microphone so agent can hear us
      await this.publishMicrophone();
    } catch (error) {
      console.error("LiveKit connection failed:", error);

      const message =
        error instanceof Error ? error.message : "Failed to connect to avatar";

      trackError("livekit", "connection", message);
      this.callbacks.onError?.(message);
      this.callbacks.onStateChange?.("error");
      this.room = null;
      this.connectionStartTime = null;

      throw error;
    }
  }

  private async publishMicrophone(): Promise<void> {
    if (!this.room) return;

    try {
      console.log("Publishing microphone to room...");
      const tracks = await createLocalTracks({
        audio: true,
        video: false,
      });

      for (const track of tracks) {
        if (track.kind === Track.Kind.Audio) {
          this.localAudioTrack = track;
          await this.room.localParticipant.publishTrack(track);
          console.log("Microphone published to room");
        }
      }
    } catch (error) {
      console.error("Failed to publish microphone:", error);
      // Don't fail the whole connection, but log the error
      trackError("microphone", "publish", String(error));
    }
  }

  private setupEventListeners(): void {
    if (!this.room) return;

    this.room.on(
      RoomEvent.TrackSubscribed,
      (
        track: RemoteTrack,
        _publication: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        console.log(
          `Track subscribed: ${track.kind} from ${participant.identity}`
        );

        const isAvatarParticipant =
          participant.identity.toLowerCase().includes("hedra") ||
          participant.identity.toLowerCase().includes("avatar") ||
          participant.identity.toLowerCase().includes("agent");

        if (track.kind === Track.Kind.Video && isAvatarParticipant) {
          console.log("Avatar video track received");
          const stream = new MediaStream([track.mediaStreamTrack]);
          this.videoElement.srcObject = stream;
          this.videoElement.autoplay = true;
          this.videoElement.playsInline = true;

          void this.videoElement.play().catch((err) => {
            console.error("Video play error:", err);
          });

          // Track connection time
          const connectionTime = this.connectionStartTime
            ? Date.now() - this.connectionStartTime
            : 0;
          trackAvatarConnected(connectionTime);

          this.callbacks.onStreamReady?.();
          this.callbacks.onStateChange?.("active");
        }

        if (track.kind === Track.Kind.Audio && isAvatarParticipant) {
          console.log("Avatar audio track received, attaching for playback");
          track.attach();
        }
      }
    );

    this.room.on(
      RoomEvent.TrackUnsubscribed,
      (
        track: RemoteTrack,
        _publication: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        const isAvatarParticipant =
          participant.identity.toLowerCase().includes("hedra") ||
          participant.identity.toLowerCase().includes("avatar") ||
          participant.identity.toLowerCase().includes("agent");

        if (track.kind === Track.Kind.Video && isAvatarParticipant) {
          console.log("Avatar video track unsubscribed");
          this.callbacks.onDisconnected?.();
          this.callbacks.onStateChange?.("idle");
        }

        if (track.kind === Track.Kind.Audio && isAvatarParticipant) {
          console.log("Avatar audio track unsubscribed, detaching");
          track.detach();
        }
      }
    );

    this.room.on(RoomEvent.Disconnected, () => {
      console.log("Disconnected from LiveKit room");
      trackAvatarDisconnected("room_disconnected");
      this.callbacks.onDisconnected?.();
      this.callbacks.onStateChange?.("idle");
    });

    // Listen for agent state messages
    this.room.on(
      RoomEvent.DataReceived,
      (payload: Uint8Array, participant?: RemoteParticipant) => {
        try {
          const message = new TextDecoder().decode(payload);
          console.log(
            `[DataReceived] From ${participant?.identity ?? "unknown"}: ${message}`
          );

          // Handle agent state updates
          if (message === AGENT_MESSAGES.SPEAKING) {
            console.log("[Agent] Speaking");
            this.callbacks.onAgentSpeaking?.();
            this.callbacks.onStateChange?.("speaking");
          } else if (message === AGENT_MESSAGES.LISTENING) {
            console.log("[Agent] Listening");
            this.callbacks.onAgentListening?.();
            this.callbacks.onStateChange?.("listening");
          } else if (message === AGENT_MESSAGES.THINKING) {
            console.log("[Agent] Thinking");
            this.callbacks.onStateChange?.("processing");
          }
        } catch (error) {
          console.error("[DataReceived] Error processing message:", error);
        }
      }
    );

    // Listen for participant attribute changes (agent state)
    this.room.on(
      RoomEvent.ParticipantAttributesChanged,
      (
        changedAttributes: Record<string, string>,
        participant: RemoteParticipant | { identity: string }
      ) => {
        const isAgent = participant.identity.toLowerCase().includes("agent");
        if (isAgent && changedAttributes["lk.agent.state"]) {
          const agentState = changedAttributes["lk.agent.state"];
          console.log("[Agent State]", agentState);

          if (agentState === "speaking") {
            this.callbacks.onAgentSpeaking?.();
            this.callbacks.onStateChange?.("speaking");
          } else if (agentState === "listening") {
            this.callbacks.onAgentListening?.();
            this.callbacks.onStateChange?.("listening");
          } else if (agentState === "thinking") {
            this.callbacks.onStateChange?.("processing");
          }
        }
      }
    );
  }

  async destroy(): Promise<void> {
    // Unpublish local audio track
    if (this.localAudioTrack) {
      this.localAudioTrack.stop();
      this.localAudioTrack = null;
    }

    if (this.room) {
      this.room.disconnect();
      this.room = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }
  }

  isInitialized(): boolean {
    return this.room !== null;
  }

  // Mute/unmute microphone
  async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    if (this.localAudioTrack) {
      if (enabled) {
        this.localAudioTrack.unmute();
      } else {
        this.localAudioTrack.mute();
      }
    }
  }
}
